<template>
  <div class="ai-workspace">
    <section class="ai-hero">
      <div class="ai-hero__copy">
        <p class="ai-hero__eyebrow">Modul AI</p>
        <h1 class="ai-hero__title">Workspace AI separat pentru review, runs și setări provider</h1>
        <p class="ai-hero__subtitle">
          Toată operațiunea AI rămâne aici. CRM-ul primește doar rezultatul aprobat explicit.
        </p>
      </div>
      <div class="ai-hero__actions">
        <v-btn variant="tonal" color="primary" :loading="isRefreshingAll" @click="refreshCurrentTab">
          Reîmprospătează
        </v-btn>
      </div>
    </section>

    <v-alert
      v-if="launchContext.active"
      type="info"
      class="mb-4"
      closable
      @click:close="clearLaunchContext"
    >
      <div class="d-flex flex-wrap align-center justify-space-between ga-3">
        <div>
          <div class="text-subtitle-2">{{ $t("ncrm.ai.workspace.contextTitle") }}</div>
          <div class="text-body-2">
            {{ launchContext.description }}
          </div>
        </div>
        <div class="d-flex flex-wrap ga-2">
          <v-btn
            v-if="launchContext.runId && activeTab !== RUNS_TAB"
            size="small"
            variant="tonal"
            color="primary"
            @click="focusContextRun"
          >
            {{ $t("ncrm.ai.actions.viewRun") }}
          </v-btn>
          <v-btn
            v-if="launchContext.sourcePath"
            size="small"
            variant="text"
            color="primary"
            @click="openSourceRecord"
          >
            {{ $t("ncrm.ai.actions.openSource") }}
          </v-btn>
        </div>
      </div>
    </v-alert>

    <v-alert
      v-if="feedback.message"
      :type="feedback.type"
      class="mb-4"
      closable
      @click:close="clearFeedback"
    >
      {{ feedback.message }}
    </v-alert>

    <div class="ai-tabs">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        type="button"
        class="ai-tab"
        :class="{ 'ai-tab--active': activeTab === tab.id }"
        @click="setActiveTab(tab.id)"
      >
        <v-icon size="18">{{ tab.icon }}</v-icon>
        <span>{{ tab.label }}</span>
      </button>
    </div>

    <AiReviewPanel
      v-if="activeTab === REVIEW_TAB"
      ref="reviewPanel"
      @feedback="handleFeedback"
    />
    <AiRunsPanel
      v-else-if="activeTab === RUNS_TAB"
      ref="runsPanel"
      @feedback="handleFeedback"
    />
    <AiPromptsPanel
      v-else-if="activeTab === PROMPTS_TAB"
      ref="promptsPanel"
      @feedback="handleFeedback"
    />
    <template v-else>
      <!-- DPIA A4 (riscul R9): furnizorii AI primesc conținut din CRM și
           prelucrează în afara țării. Consecința se arată aici, unde se lipește
           cheia, nu doar în contract. -->
      <NcrmIntegrationPrivacyNotice integration-key="ai" />
      <AiSettingsPanel ref="settingsPanel" @feedback="handleFeedback" />
    </template>
  </div>
</template>

<script>
import "./ai-workspace.css";
import AiReviewPanel from "./AiReviewPanel.vue";
import AiRunsPanel from "./AiRunsPanel.vue";
import AiPromptsPanel from "./AiPromptsPanel.vue";
import AiSettingsPanel from "./AiSettingsPanel.vue";
import NcrmIntegrationPrivacyNotice from "@/components/ncrm/integrations/NcrmIntegrationPrivacyNotice.vue";
import { resolveAiLaunchContext } from "@/Utile/aiAssist.mjs";
import { AI_TABS, REVIEW_TAB, RUNS_TAB, PROMPTS_TAB, SETTINGS_TAB } from "./aiWorkspaceShared.mjs";

const VALID_TABS = new Set([REVIEW_TAB, RUNS_TAB, PROMPTS_TAB, SETTINGS_TAB]);

export default {
  name: "AiWorkspace",
  components: {
    AiReviewPanel,
    AiRunsPanel,
    AiPromptsPanel,
    AiSettingsPanel,
    NcrmIntegrationPrivacyNotice,
  },
  data() {
    return {
      tabs: AI_TABS,
      REVIEW_TAB,
      RUNS_TAB,
      PROMPTS_TAB,
      activeTab: REVIEW_TAB,
      feedback: {
        type: "success",
        message: "",
      },
      isRefreshingAll: false,
    };
  },
  watch: {
    "$route.query.tab"() {
      this.syncFromRoute();
    },
  },
  computed: {
    launchContext() {
      return resolveAiLaunchContext(this.$route.query);
    },
  },
  mounted() {
    this.syncFromRoute();
  },
  methods: {
    clearLaunchContext() {
      const nextQuery = { ...this.$route.query };
      delete nextQuery.source;
      delete nextQuery.sourcePath;
      delete nextQuery.entityType;
      delete nextQuery.entityId;
      delete nextQuery.entityLabel;
      delete nextQuery.profileKey;
      this.$router.replace({ query: nextQuery });
    },
    clearFeedback() {
      this.feedback = {
        type: "success",
        message: "",
      };
    },
    focusContextRun() {
      if (!this.launchContext?.runId) {
        return;
      }
      this.$router.replace({
        query: {
          ...this.$route.query,
          tab: RUNS_TAB,
          runId: String(this.launchContext.runId),
        },
      });
    },
    handleFeedback(payload) {
      if (!payload?.message) {
        return;
      }
      this.feedback = {
        type: payload.type || "success",
        message: payload.message,
      };
    },
    syncFromRoute() {
      const nextTab = VALID_TABS.has(this.$route.query?.tab) ? this.$route.query.tab : REVIEW_TAB;
      if (this.activeTab !== nextTab) {
        this.activeTab = nextTab;
      }
    },
    openSourceRecord() {
      if (!this.launchContext?.sourcePath) {
        return;
      }
      this.$router.push({ path: this.launchContext.sourcePath }).catch(() => {});
    },
    setActiveTab(tabId) {
      if (tabId === this.activeTab) {
        return;
      }

      const nextQuery = { ...this.$route.query, tab: tabId };
      if (tabId !== REVIEW_TAB) {
        delete nextQuery.reviewId;
      }
      if (tabId !== RUNS_TAB) {
        delete nextQuery.runId;
      }
      this.$router.replace({ query: nextQuery });
    },
    async refreshCurrentTab() {
      this.isRefreshingAll = true;
      try {
        const activePanel =
          this.activeTab === REVIEW_TAB
            ? this.$refs.reviewPanel
            : this.activeTab === RUNS_TAB
              ? this.$refs.runsPanel
              : this.activeTab === PROMPTS_TAB
                ? this.$refs.promptsPanel
                : this.$refs.settingsPanel;

        if (activePanel?.refreshPanel) {
          await activePanel.refreshPanel();
        }
      } finally {
        this.isRefreshingAll = false;
      }
    },
  },
};
</script>
